import { SIMAElementClassifier, SIMAParcelType, } from "../types";
import { SurveyPoint } from "./survey-point";
import { SurveyPointIndex } from "./survey-point-index";
/**
 * 画地境界点 (D01 レコード)。
 * キャメルケースの直接フィールドを持ち、toPointData() でワイヤフォーマットに変換します。
 */
export class ParcelPoint {
    classifier = SIMAElementClassifier.PARCEL_POINT;
    pointNumber;
    pointName;
    surveyPoint;
    constructor(data, surveyPoint) {
        const input = data;
        this.pointNumber = input?.pointNumber ?? input?.point_number ?? 0;
        this.pointName = input?.pointName ?? input?.point_name ?? "";
        this.surveyPoint = surveyPoint;
    }
    get x() {
        return this.surveyPoint?.x;
    }
    get y() {
        return this.surveyPoint?.y;
    }
    get z() {
        return this.surveyPoint?.z;
    }
    toPointData() {
        return {
            classifier: this.classifier,
            point_number: this.pointNumber,
            point_name: this.pointName,
        };
    }
}
/**
 * 画地・筆 (D00..D99 ブロック)。
 * 内部 begin 構造体をフラット化し、直接フィールドとして保持します。
 */
export class Parcel {
    parcelNumber;
    lotNumber;
    parcelType;
    parcelName;
    points = [];
    _surveyPointIndex;
    constructor(data, surveyPointsOrIndex) {
        const input = data;
        const begin = input?.begin;
        this.parcelNumber = input?.parcelNumber ?? begin?.parcel_number ?? 1;
        this.lotNumber = input?.lotNumber ?? begin?.lot_number ?? 1;
        this.parcelType =
            input?.parcelType ?? begin?.parcel_type ?? SIMAParcelType.CLOSED;
        this.parcelName = input?.parcelName ?? begin?.parcel_name;
        if (data?.points) {
            this.points = data.points.map((p) => p instanceof ParcelPoint ? p : new ParcelPoint(p));
        }
        if (surveyPointsOrIndex) {
            this.resolvePoints(surveyPointsOrIndex);
        }
    }
    get isClosed() {
        return this.parcelType === SIMAParcelType.CLOSED;
    }
    get key() {
        return `${this.parcelNumber}-${this.lotNumber}`;
    }
    addPoint(point) {
        let instance;
        if (point instanceof ParcelPoint) {
            instance = point;
        }
        else if (point instanceof SurveyPoint) {
            instance = new ParcelPoint({
                pointNumber: point.pointNumber,
                pointName: point.pointName,
            }, point);
        }
        else {
            instance = new ParcelPoint(point);
        }
        if (this._surveyPointIndex && !instance.surveyPoint) {
            const matched = this._surveyPointIndex.lookup(instance.pointNumber, instance.pointName);
            if (matched) {
                instance.surveyPoint = matched;
            }
        }
        this.points.push(instance);
        return instance;
    }
    /**
     * SurveyPointIndex または SurveyPoint 配列を用いて画地構成点の座標を解決します。
     */
    resolvePoints(indexOrPoints) {
        const index = Array.isArray(indexOrPoints)
            ? new SurveyPointIndex(indexOrPoints)
            : indexOrPoints;
        this._surveyPointIndex = index;
        for (const pt of this.points) {
            const matched = index.lookup(pt.pointNumber, pt.pointName);
            if (matched) {
                pt.surveyPoint = matched;
            }
        }
    }
    /**
     * 画地座標が解決されている場合、2D 靴ひも公式 (Shoelace formula) により面積 (m²) を計算します。
     */
    calculateArea() {
        if (this.points.length < 3)
            return undefined;
        const coords = this.points
            .map((p) => p.surveyPoint ? { x: p.surveyPoint.x, y: p.surveyPoint.y } : null)
            .filter((c) => c !== null);
        if (coords.length !== this.points.length)
            return undefined;
        let area = 0;
        const n = coords.length;
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            area += coords[i].x * coords[j].y;
            area -= coords[j].x * coords[i].y;
        }
        return Math.abs(area) / 2.0;
    }
    /**
     * ワイヤフォーマット (ISIMAParcel 構造) に変換します。
     */
    toParcelData() {
        return {
            begin: {
                classifier: SIMAElementClassifier.PARCEL_BEGIN,
                parcel_number: this.parcelNumber,
                lot_number: this.lotNumber,
                parcel_type: this.parcelType,
                parcel_name: this.parcelName,
            },
            points: this.points.map((p) => p.toPointData()),
        };
    }
}
//# sourceMappingURL=parcel.js.map